import { UzButton, UzCollapsibleSection, UzInput, UzSelect } from '@utazzo/ui';
import { Plane, RefreshCw, TrainFront } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useCityTransport, useResyncCityTransport, useSetCityCentralStation } from '../use-cities';

import { formatDMY } from '@/lib/date-format';

const AUTO = '__auto';
const MANUAL = '__manual';

/**
 * City master: the two transport anchors hotel cards measure to.
 *
 * - Central railway station: auto-picked from OpenStreetMap; an operator can
 *   pick a different candidate or enter one OSM missed. Saves immediately
 *   (separate from the drawer's "Save changes") and survives OSM re-syncs.
 * - Airports: read-only list of the commercial airports (with IATA codes) the
 *   sync found; each hotel card names whichever is nearest that hotel.
 */
export const CityTransportSection = ({ cityId }: { cityId: string }): React.JSX.Element => {
  const transport = useCityTransport(cityId);
  const setCentral = useSetCityCentralStation(cityId);
  const resync = useResyncCityTransport(cityId);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ name: '', latitude: '', longitude: '' });

  const data = transport.data;
  const busy = setCentral.isPending || resync.isPending;

  const options = useMemo(() => {
    const stations = (data?.stations ?? []).map((s) => ({
      value: s.id,
      label:
        `${s.name}` +
        (s.distanceFromCentreKm !== null ? ` · ${s.distanceFromCentreKm} km from centre` : '') +
        (s.source === 'MANUAL' ? ' · set by ops' : ''),
    }));
    return [
      { value: AUTO, label: 'Auto-pick (best match from OpenStreetMap)' },
      ...stations,
      { value: MANUAL, label: 'Enter a station manually…' },
    ];
  }, [data?.stations]);

  const onSelect = (value: string): void => {
    if (value === MANUAL) {
      setManualOpen(true);
      return;
    }
    setManualOpen(false);
    if (value === AUTO) setCentral.mutate({ mode: 'auto' });
    else setCentral.mutate({ mode: 'pick', landmarkId: value });
  };

  const manualLat = Number(manual.latitude);
  const manualLng = Number(manual.longitude);
  const manualValid =
    manual.name.trim().length >= 2 &&
    manual.latitude.trim() !== '' &&
    manual.longitude.trim() !== '' &&
    Number.isFinite(manualLat) &&
    Number.isFinite(manualLng) &&
    Math.abs(manualLat) <= 90 &&
    Math.abs(manualLng) <= 180;

  const syncedLabel = data?.syncedAt
    ? `Last synced ${formatDMY(data.syncedAt)}`
    : 'Not synced yet - the first hotel search for this city syncs it, or sync now.';

  return (
    <UzCollapsibleSection
      title="Hotel location: station & airport"
      description="Hotel cards show the distance to the central railway station and the nearest airport."
      actions={
        <UzButton
          size="sm"
          variant="ghost"
          onClick={() => resync.mutate()}
          loading={resync.isPending}
          disabled={busy || data?.hasCoordinates === false}
          title={
            data?.hasCoordinates === false
              ? 'This city has no coordinates, so nothing can be synced'
              : 'Re-pull airports and mainline stations from OpenStreetMap'
          }
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Re-sync
        </UzButton>
      }
    >
      {transport.isLoading ? (
        <p className="text-xs text-zinc-500">Loading…</p>
      ) : transport.isError || !data ? (
        <p className="text-xs text-red-600">Could not load station and airport data.</p>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-zinc-500">{syncedLabel}</p>

          <div className="space-y-2">
            <UzSelect
              label="Central railway station"
              hint={
                data.stations.length === 0
                  ? 'No mainline railway station found near this city. Hotel cards will show the airport only, unless you enter one.'
                  : 'Auto-pick prefers well-known main stations closest to the city centre. Your choice is kept on re-sync.'
              }
              placeholder="No central station"
              options={options}
              value={manualOpen ? MANUAL : (data.centralStationId ?? undefined)}
              onValueChange={onSelect}
              disabled={busy}
            />
            {manualOpen && (
              <div className="space-y-2 rounded-lg border border-zinc-200 bg-zinc-50 p-2.5">
                <UzInput
                  label="Station name"
                  placeholder="e.g. Mumbai CSMT"
                  value={manual.name}
                  onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
                  disabled={busy}
                />
                <div className="grid grid-cols-2 gap-2">
                  <UzInput
                    label="Latitude"
                    placeholder="18.940"
                    inputMode="decimal"
                    value={manual.latitude}
                    onChange={(e) => setManual((m) => ({ ...m, latitude: e.target.value }))}
                    disabled={busy}
                  />
                  <UzInput
                    label="Longitude"
                    placeholder="72.835"
                    inputMode="decimal"
                    value={manual.longitude}
                    onChange={(e) => setManual((m) => ({ ...m, longitude: e.target.value }))}
                    disabled={busy}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <UzButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setManualOpen(false)}
                    disabled={busy}
                  >
                    Cancel
                  </UzButton>
                  <UzButton
                    size="sm"
                    disabled={!manualValid || busy}
                    loading={setCentral.isPending}
                    onClick={() =>
                      setCentral.mutate(
                        {
                          mode: 'manual',
                          name: manual.name.trim(),
                          latitude: manualLat,
                          longitude: manualLng,
                        },
                        { onSuccess: () => setManualOpen(false) },
                      )
                    }
                  >
                    Save station
                  </UzButton>
                </div>
              </div>
            )}
            {!manualOpen && data.centralStationId && (
              <p className="flex items-center gap-1 text-[11px] text-zinc-600">
                <TrainFront className="h-3 w-3 text-zinc-400" aria-hidden />
                Hotel cards measure to{' '}
                <span className="font-medium">
                  {data.stations.find((s) => s.id === data.centralStationId)?.name}
                </span>
              </p>
            )}
          </div>

          <div>
            <p className="mb-1 text-xs font-medium text-zinc-700">Airports</p>
            {data.airports.length === 0 ? (
              <p className="text-[11px] text-zinc-500">
                No commercial airport found within 150 km.
              </p>
            ) : (
              <ul className="space-y-1">
                {data.airports.map((a) => (
                  <li key={a.id} className="flex items-center gap-1.5 text-[11px] text-zinc-700">
                    <Plane className="h-3 w-3 text-zinc-400" aria-hidden />
                    <span className="font-semibold">{a.iataCode}</span>
                    <span>{a.name}</span>
                    {a.distanceFromCentreKm !== null && (
                      <span className="text-zinc-500">
                        · {a.distanceFromCentreKm} km from centre
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-[11px] text-zinc-500">
              Each hotel card names whichever of these is nearest that hotel.
            </p>
          </div>
        </div>
      )}
    </UzCollapsibleSection>
  );
};
